import { useEffect, useId, useRef, useState } from 'react';
import type { Day } from '../../fixtures/plan';
import { ClearLogPrompt, clearLogLine } from './ClearLogPrompt';
import { registerPlanText } from './dayDetailsText';
import { daySections, weatherRowText, type PanelDay } from './dayDetailsDisplay';
import { beginDayWrite, endDayWrite, useDayWrite } from './dayWrites';
import { daySessionLine } from '../../lib/weekMeasure';
import { contextCoord } from '../../lib/contextCoords';
import { useAthleteContext } from '../../hooks/useAthleteContext';
import { useHeroWeather } from '../../hooks/useHeroWeather';
import { AppleWeatherMark } from '../attribution/AppleWeatherMark';
import styles from './DayDetails.module.css';

/**
 * B309 — the week view's day details. A tap on a day (today included) selects
 * it and this panel opens below the grid / the list / the chips, in the same
 * place in all three layouts, so no row or cell ever moves when it opens,
 * switches or closes. It is the ONLY place the week view changes completion:
 * the done / not done button, a deliberate press, never a tap on the day itself.
 *
 * The panel is read-only apart from that button. It shows the session and, when
 * logged, completion, effort (RPE) and actual distance, every number in the
 * athlete's own unit (the adapter states `mileageInUnit`, `details` and
 * `actualDistance` that way). The button calls the same `onToggle` the week
 * view always took (`usePlanDayCompletion.toggle` through today.tsx
 * `handleToggleDay`), so the effort prompt that follows a completing toggle,
 * the refetches and the server write are all the existing flow.
 *
 * From the press until `onToggle` settles (handleToggleDay settles when the
 * write has landed AND the week refetch has landed or failed) the day is "in
 * flight": the panel shows the NEW status at once (optimistic), and the button
 * is unavailable. A second press cannot write: the guard is per plan day and
 * lives outside the panel (`dayWrites`), so closing the panel or opening
 * another day and coming back during the refetch finds the same state. A
 * rejected write rolls the display back to the day as it was, shows an error
 * line, and makes the button available again. The button is `aria-disabled`,
 * never `disabled`, so it keeps focus. After a write the panel stays open.
 *
 * Ruling 1b: pressing "Mark as not done" on a day with a logged effort or
 * distance asks first (`ClearLogPrompt`, in place of the button): "Keep it"
 * writes nothing, "Clear it" is the write above. Nothing logged, no question.
 *
 * DAY-DETAILS-1019 (v2 only): the panel shows the day's stored details (Claude Design return 2026-10-19, Ask 2).
 * Which sections there are, their words, and the pace once are `dayDetailsDisplay.ts`'s (`daySections`); this file
 * draws them. The legacy panel (no `variant`) is untouched.
 */

/** A token that is only dashes and spaces says nothing; it is dropped from the title. */
const PLACEHOLDER = /^[\s–—-]*$/;

export type DayDetailsProps = {
  day: PanelDay;
  /** Same contract as WeekPlan's `onToggle`. Absent → no button, panel is read-only. */
  onToggle?: (dayId: string, currentStatus: Day['completionStatus']) => Promise<unknown>;
  /** DOM id, so the selected day's cell can point at its panel with aria-controls. */
  id?: string;
  /**
   * W1-TODAY-WEEK (TW-4, frame 1s) — `'v2'` is the 2.0 panel: one verb ("Mark done",
   * "Undo"), "✓ Done" for a finished day, "Planned" and no control for a future day.
   * Absent: the panel exactly as it always was. The write path is the same one either
   * way (`dayWrites` guard, then `onToggle`); the client stops offering the control
   * for a future day, and the server still accepts any day (B218: no refusal here).
   */
  variant?: 'v2';
  /**
   * v2 only. The day is after today in its week, derived from position by the caller
   * (`isFutureDay`). A future day that is not done shows the plan and no control. A
   * future day that is already done (marked ahead before this rule) keeps "✓ Done" and
   * "Undo", so a mistaken mark can still be taken back.
   */
  future?: boolean;
  /**
   * v2 only. DAY-DETAILS-1019 (frame 3b): the panel head carries the Weather row, with the Apple Weather mark,
   * when the day is today and not a rest day. Train sets it (the cards and the table); Today does not, because the
   * hero above the Today week list already shows the row. The row is absent while there is no weather to show
   * (no stored location, flag off, a failed fetch), never a placeholder.
   */
  weatherRow?: boolean;
  /**
   * v2 only. The "Ask about this run" door (strings 10 and 11): drawn in the footer when a handler is given,
   * absent otherwise. No caller passes one until the 2.0 chat can open with a day attached, which is what keeps
   * the door hidden. Never given on a read-only view of someone else's plan (frame 2d).
   */
  onAsk?: () => void;
};

/**
 * Frame 3b — the Weather row in the day panel head. Its own component so the weather hook runs only for the one
 * panel that shows it (today's, opened from Train). Same reads as the Today hero: the stored location
 * (`_latitude` and `_longitude`, coerced the way the server does) through `useHeroWeather`, and the same value
 * words. Nothing renders until weather resolves; the Apple Weather mark rides the value and never stands alone.
 */
function PanelWeatherRow() {
  const { raw } = useAthleteContext();
  const { data } = useHeroWeather({
    enabled: true,
    latitude: contextCoord(raw._latitude),
    longitude: contextCoord(raw._longitude),
  });
  if (!data) return null;
  return (
    <div className={styles.weatherV2}>
      <p className={styles.weatherRowV2}>
        <span className={styles.labelV2}>Weather</span>
        <span className={styles.weatherValueV2}>{weatherRowText(data)}</span>
      </p>
      <p className={styles.weatherLegalV2}>
        <AppleWeatherMark />
      </p>
    </div>
  );
}

/**
 * Frames 2a to 2c — "The session" as a stepped rail: Warm-up, Main set, Cool-down, each only when the day stores
 * it. The main set is the filled dot, the others ringed. At the AX attribute the dots go and each step carries a
 * left rule instead (CSS). Its own component, so the `useId` for the list's label runs for a v2 panel only and
 * the legacy panel's ids are what they always were.
 */
function SessionRail({ steps }: { steps: ReturnType<typeof daySections>['steps'] }) {
  const labelId = useId();
  return (
    <div className={styles.sessionV2}>
      <span className={styles.labelV2} id={labelId}>
        The session
      </span>
      <ol className={styles.railV2} role="list" aria-labelledby={labelId}>
        {steps.map((s) => (
          <li key={s.key} className={styles.stepV2} data-step={s.key}>
            <span className={styles.markV2} aria-hidden="true">
              <span className={s.key === 'main' ? styles.dotMainV2 : styles.dotV2} />
            </span>
            <span className={styles.stepBodyV2}>
              <span className={styles.stepLabelV2}>{s.label}</span>
              <span className={styles.stepTextV2}>{s.text}</span>
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}

export function DayDetails({ day, onToggle, id, variant, future, weatherRow, onAsk }: DayDetailsProps) {
  const [failed, setFailed] = useState(false);
  // The in-flight write for this plan day, if any (shared with every panel for it).
  const write = useDayWrite(day.id);
  const pending = !!write;
  const mounted = useRef(true);
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    mounted.current = true;
    // The panel sits below the day selector; on a long page that can be below
    // the fold, where a tap would look like it did nothing. Scroll only as far
    // as needed to bring it into view (no-op when it already is).
    panelRef.current?.scrollIntoView?.({ block: 'nearest' });
    return () => {
      mounted.current = false;
    };
  }, []);

  // The status shown: the in-flight write's target while one is running (the
  // same rule `usePlanDayCompletion.toggle` applies), else the day as given.
  const status = write ? write.status : day.completionStatus;
  const isCompleted = status === 'completed';
  const isSkipped = status === 'skipped';
  const dateLabel = `${day.dayShort} ${day.dayNumber}`;
  const title = [day.label, day.mileageInUnit ?? day.mileage]
    .filter((t) => !PLACEHOLDER.test(t))
    .join(' · ');
  const body = registerPlanText(day.details);
  const hasRpe = typeof day.rpe === 'number';
  const distance = day.actualDistance;
  const hasDistance = !!distance && distance.value > 0;

  // Ruling 1b: un-completing a day that has a logged effort or distance asks
  // first. The line is null (no prompt) when nothing is logged or when the press
  // would complete the day.
  const clearLine = isCompleted ? clearLogLine(day) : null;
  const [confirming, setConfirming] = useState(false);
  const mainRef = useRef<HTMLButtonElement>(null);
  const restoreFocus = useRef(false);
  const showConfirm = confirming && !!clearLine && !pending;
  // Back from the prompt (either answer): the done / not done button gets focus.
  useEffect(() => {
    if (!showConfirm && restoreFocus.current) {
      restoreFocus.current = false;
      mainRef.current?.focus();
    }
  }, [showConfirm]);
  // The day stopped being a completed one with a log (the week refetched, or a
  // write landed): nothing to ask any more.
  useEffect(() => {
    if (!clearLine) setConfirming(false);
  }, [clearLine]);

  const handleClick = async () => {
    if (!onToggle || !day.id) return;
    const dayId = day.id;
    // Claim the day synchronously: a second press (a double click, a press from
    // a reopened panel, a press during the refetch) gets false and does nothing.
    if (!beginDayWrite(dayId, day.completionStatus === 'completed' ? 'upcoming' : 'completed')) return;
    setFailed(false);
    try {
      await onToggle(dayId, day.completionStatus);
    } catch {
      // Rolled back below by releasing the day: the display returns to the day
      // as given, and the athlete is told it did not save.
      if (mounted.current) setFailed(true);
    } finally {
      endDayWrite(dayId);
    }
  };

  // The press on the done / not done button: ask first when it would clear a log.
  const press = () => {
    if (pending) return;
    if (clearLine) {
      setConfirming(true);
      return;
    }
    void handleClick();
  };
  // "Keep it" writes nothing; "Clear it" is the existing un-complete write.
  const answer = (clear: boolean) => {
    restoreFocus.current = true;
    setConfirming(false);
    if (clear) void handleClick();
  };

  if (variant === 'v2') {
    // Strings 14, 40, 42 to 49 of the ratified Today set. Day label in title case
    // ("Wed 14"); the label role's CSS uppercases it.
    const dayName = day.dayShort.charAt(0) + day.dayShort.slice(1).toLowerCase();
    const dateLabelV2 = `${dayName} ${day.dayNumber}`;
    const titleV2 = daySessionLine(day, ' · ');
    // B375: a future day that is not done has nothing to mark; "Planned" says so.
    const futureLocked = !!future && day.completionStatus !== 'completed';
    const showPlanned = !!future && !isCompleted;
    // Ruling 11 (2026-10-17): a rest day has nothing to mark either. A rest day
    // already marked done keeps "Undo", so a mistaken mark can be taken back.
    const restLocked = !!day.rest && day.completionStatus !== 'completed';
    const offerControl = !!onToggle && !!day.id && !futureLocked && !restLocked;
    // DAY-DETAILS-1019 (Ask 2): the day's stored details, in the frames' order. Each section is there only
    // when its field is non-empty after trim.
    const s = daySections(day);
    const showWeather = !!weatherRow && !!day.today && !s.rest;
    const showLogged = hasRpe || hasDistance;
    const showSections = !!s.why || s.steps.length > 0 || !!s.recover || showLogged;
    const showFooter = offerControl || !!onAsk;
    return (
      <div
        ref={panelRef}
        className={styles.panelV2}
        id={id}
        role="region"
        aria-label={`${dateLabelV2} details`}
      >
        <div className={styles.headV2}>
          <span className={styles.dateV2}>{dateLabelV2}</span>
          {(showPlanned || isCompleted || isSkipped) && (
            <span className={styles.statusV2}>
              {showPlanned && <span className={styles.plannedV2}>Planned</span>}
              {isCompleted && <span className={styles.doneV2}>✓ Done</span>}
              {isSkipped && <span className={styles.skippedV2}>Skipped</span>}
            </span>
          )}
          {titleV2 && <span className={styles.titleV2}>{titleV2}</span>}
          {s.pace && (
            <p className={styles.paceV2}>
              <span className={styles.labelV2}>Pace</span>
              <span className={`${styles.paceValueV2} tnum`}>{s.pace}</span>
            </p>
          )}
          {showWeather && <PanelWeatherRow />}
        </div>
        {s.adjusted && (
          <div className={styles.adjustedV2}>
            <span className={styles.adjustedLabelV2}>Adjusted</span>
            <p className={styles.adjustedTextV2}>{s.adjusted}</p>
          </div>
        )}
        {!s.any && !s.rest && <p className={styles.bodyEmptyV2}>No further details for this day.</p>}
        {showSections && (
          <div className={styles.sectionsV2} data-steps={s.steps.length > 0 ? 'yes' : 'no'}>
            {s.why && (
              <div className={styles.whyV2}>
                <span className={styles.labelV2}>{s.whyLabel}</span>
                <p className={styles.whyTextV2}>{s.why}</p>
              </div>
            )}
            {s.steps.length > 0 && <SessionRail steps={s.steps} />}
            {s.recover && (
              <div className={styles.recoverV2}>
                <span className={styles.labelV2}>Recover</span>
                <p className={styles.recoverTextV2}>{s.recover}</p>
              </div>
            )}
            {showLogged && (
              <dl className={styles.loggedV2}>
                {hasRpe && (
                  <>
                    <dt>Effort</dt>
                    <dd className="tnum">{day.rpe} of 10</dd>
                  </>
                )}
                {hasDistance && (
                  <>
                    <dt>Actual distance</dt>
                    <dd className="tnum">
                      {distance.value} {distance.unit}
                    </dd>
                  </>
                )}
              </dl>
            )}
          </div>
        )}
        {showFooter && (
          <div className={styles.footerV2}>
            {offerControl && (
              <div className={styles.actionsV2}>
                {showConfirm && clearLine && (
                  <ClearLogPrompt
                    line={clearLine}
                    onClear={() => answer(true)}
                    onKeep={() => answer(false)}
                  />
                )}
                <button
                  ref={mainRef}
                  type="button"
                  className={isCompleted ? styles.undoV2 : styles.actionV2}
                  onClick={press}
                  // The prompt stands in for the button while it is open (hidden, not
                  // unmounted), and a write in flight is aria-disabled, never disabled,
                  // so the button keeps focus: both exactly as the legacy panel has them.
                  hidden={showConfirm}
                  aria-disabled={pending || undefined}
                >
                  {isCompleted ? 'Undo' : 'Mark done'}
                </button>
                {failed && (
                  <p className={styles.errorV2} role="alert">
                    Could not save this change. Try again.
                  </p>
                )}
              </div>
            )}
            {onAsk && (
              <button type="button" className={styles.askV2} onClick={onAsk}>
                {s.askLabel}
                <span aria-hidden="true"> ›</span>
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={panelRef}
      className={styles.panel}
      id={id}
      role="region"
      aria-label={`${dateLabel} details`}
    >
      <div className={styles.head}>
        <span className={styles.date}>{dateLabel}</span>
        {title && <span className={styles.title}>{title}</span>}
        {isCompleted && <span className={styles.done}>✓ Completed</span>}
        {isSkipped && <span className={styles.skipped}>Skipped</span>}
      </div>
      {body ? (
        <p className={styles.body}>{body}</p>
      ) : (
        <p className={styles.bodyEmpty}>No further details for this day.</p>
      )}
      {(hasRpe || hasDistance) && (
        <dl className={styles.logged}>
          {hasRpe && (
            <div className={styles.loggedRow}>
              <dt>Effort</dt>
              <dd className="tnum">{day.rpe} of 10</dd>
            </div>
          )}
          {hasDistance && (
            <div className={styles.loggedRow}>
              <dt>Actual distance</dt>
              <dd className="tnum">
                {distance.value} {distance.unit}
              </dd>
            </div>
          )}
        </dl>
      )}
      {onToggle && day.id && (
        <div className={styles.actions}>
          {showConfirm && clearLine && (
            <ClearLogPrompt
              line={clearLine}
              onClear={() => answer(true)}
              onKeep={() => answer(false)}
            />
          )}
          <button
            ref={mainRef}
            type="button"
            className={isCompleted ? styles.undo : styles.action}
            onClick={press}
            // The prompt stands in for the button while it is open (hidden, not
            // unmounted, so the node and its place in the order are kept).
            hidden={showConfirm}
            // aria-disabled, not disabled: a disabled button drops keyboard focus
            // in some browsers, and the write must not take focus away. The
            // in-flight guard in handleClick is what blocks a second press.
            aria-disabled={pending || undefined}
          >
            {isCompleted ? 'Mark as not done' : 'Mark as done'}
          </button>
          {failed && (
            <p className={styles.error} role="alert">
              Could not save this change. Try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
